import {t} from 'i18next';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {Box, Grid, InputAdornment, Typography} from '@mui/material';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Search} from '@mui/icons-material';
import './NewTag.scss';
import ArrMultiSelectDropdown from '../../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {
  useGetBulkTagList,
  useGetBulkTagsDetails,
} from '../../../../hooks/assignments/useAssignments';
import {transformJsTreeApiToTreeData} from '../../actionMenuUtils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {IGetBulkTagsDetailsClassification} from '../../../../api/types/assignment.type';
import {PROPOGATION_TYPE} from '../../../../shared/constants';
import Clear from '@mui/icons-material/CancelOutlined';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';

const options: ISelectItem[] = [
  {id: PROPOGATION_TYPE.FAMILY, name: 'Family Members'},
  {id: PROPOGATION_TYPE.DUPLICATES, name: 'Exact Duplicates (Uses MD5Hash)'},
  {id: PROPOGATION_TYPE.EMAILTHREADS, name: 'Email Threads'},
  {id: PROPOGATION_TYPE.EMAILDUPLICATES, name: 'Email Duplicates'},
];

interface TreeItem {
  id: string;
  label: string;
  fileType?: string;
  isGroup?: string | boolean;
  type?: string;
  children?: TreeItem[];
}

const isGroupNode = (node: TreeItem): boolean => {
  return (
    node.isGroup === true ||
    node.isGroup === 'true' ||
    node.type === 'tags' ||
    node.fileType === 'tags' ||
    !!node.children
  );
};

interface INewTagProps {
  selectedNewTag: string;
  setSelectedNewTag: (value: string | null | undefined) => void;
  searchInputNewTags: string;
  setSearchInputNewTags: (value: string) => void;
  name: string;
  handleChangeName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isNameInputValid: boolean;
  handleBlurName: (fieldName: 'name') => void;
  setErrorMsg: () => string;
  propagateTag: ISelectItem[];
  setPropagateTag: (value: ISelectItem[]) => void;
  tagClassification: ISelectItem | null;
  setTagClassification: (value: ISelectItem | null) => void;
}

const removeLeafNodes = (nodes: TreeItem[] | undefined): TreeItem[] => {
  if (!nodes) return [];

  return nodes
    .filter(isGroupNode)
    .map((node) => ({
      ...node,
      children: removeLeafNodes(node.children),
    }));
};

const transformApiDataToTagClassificationItems = (
  classificationList: IGetBulkTagsDetailsClassification[]
): ISelectItem[] => {
  return classificationList.map((item) => ({
    id: item.key,
    name: item.value,
  }));
};

// Helper function to get expanded ids based on the search keyword
const getExpandedIdsForSearch = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
  keyword: string
): string[] => {
  const ids = new Set<string>();
  const text = keyword.toLowerCase();

  const dfs = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    const isMatch = node.label?.toLowerCase().includes(text);
    let childMatch = false;

    if (node.children?.length) {
      for (const child of node.children) {
        if (dfs(child)) childMatch = true;
      }
    }

    if (isMatch || childMatch) {
      ids.add(node.id);
      return true;
    }

    return false;
  };

  nodes.forEach((root) => dfs(root));
  return Array.from(ids);
};

// Expand all nodes on initial load
const getAllIds = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
): string[] => {
  const result: string[] = [];
  const dfs = (n: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    result.push(n.id);
    n.children?.forEach(dfs);
  };
  nodes.forEach(dfs);
  return result;
};

const NewTag: React.FC<INewTagProps> = ({
  selectedNewTag,
  setSelectedNewTag,
  searchInputNewTags,
  setSearchInputNewTags,
  name,
  handleChangeName,
  isNameInputValid,
  handleBlurName,
  setErrorMsg,
  propagateTag,
  setPropagateTag,
  tagClassification,
  setTagClassification,
}) => {
  const enBulkTag = 'actionMenu.bulkTagFlyout';
  const [searchInput, setSearchInput] = useState<string>(searchInputNewTags);
  const [treeKey, setTreeKey] = useState<number>(0);
  const [defaultExpandedIds, setDefaultExpandedIds] = useState<string[]>([]);
  const {data: BulkTagData, isPending: isLoading} =
    useGetBulkTagList('BulkTag');

  const {data: BulkTagsDetails, isPending: isTagDetailsLoading} =
    useGetBulkTagsDetails();
  const treeData = useMemo(() => {
    return transformJsTreeApiToTreeData(BulkTagData);
  }, [BulkTagData]);

  useEffect(() => {
    if (!treeData || treeData.length === 0) return;

    const allIds = getAllIds(treeData);
    setDefaultExpandedIds(allIds);
    setTreeKey((prev) => prev + 1); // remount to apply expandedIds
  }, [treeData]);

  useEffect(() => {
    const expanded = getExpandedIdsForSearch(treeData, searchInputNewTags);
    setDefaultExpandedIds(expanded);
    setTreeKey((prev) => prev + 1); // force remount to apply expandedIds
  }, [searchInputNewTags, treeData]);

  const classificationOptions = useMemo<ISelectItem[]>(() => {
    if (BulkTagsDetails?.classificationList) {
      return transformApiDataToTagClassificationItems(
        BulkTagsDetails.classificationList
      );
    }
    return [];
  }, [BulkTagsDetails]);

  const cleanedTreeData = removeLeafNodes(treeData);
  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setSearchInputNewTags(searchInput.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchInputNewTags('');
    setSearchInput('');
    setDefaultExpandedIds(getAllIds(treeData));
    setTreeKey((prev) => prev + 1); // remount tree to apply
  };

  const handleSelected = useCallback(
    (values: string[] | null) => {
      setSelectedNewTag(values?.[0]);
    },
    [setSelectedNewTag]
  );
  const handlePropagateChange = (selected: ISelectItem[]) => {
    setPropagateTag(selected);
  };

  return (
    <div className="bulk-new-tag-tab-container" data-testid="new-tag-div">
      <Box className="search-box">
        <Grid container columnSpacing={2} rowSpacing={1.5}>
          <Grid size={{xs: 12, sm: 6}}>
            <ArrTextField
              state="enabled"
              sizeVariant="medium"
              className="search-input"
              placeholder={t(`${enBulkTag}.searchPlaceholder`)}
              aria-label={t(`${enBulkTag}.searchPlaceholder`)}
              dataTestId="new-tag-search-input"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              onKeyDown={handleSearchKeyEnter}
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  startAdornment: (
                    <InputAdornment
                      position="end"
                      className="search-adornment-icon"
                    >
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment
                      position="end"
                      className="clear-adornment-icon"
                    >
                      <Clear
                        onClick={handleClearSearch}
                        style={{cursor: 'pointer'}}
                        data-testid="search-clear-btn"
                      />
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6}}></Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <Box className="custom-text-field">
              <Typography
                className="typo-text body3_size input-label"
                data-testid="input-label-name"
                tabIndex={0}
              >
                {t(`${enBulkTag}.requiredName`)}
              </Typography>
              <ArrTextField
                id="name-input"
                state={isNameInputValid ? 'error' : 'enabled'}
                dataTestId="new-tag-name-input"
                placeholder={t(`${enBulkTag}.namePlaceholder`)}
                sizeVariant="medium"
                required
                size="small"
                value={name}
                aria-required
                onChange={handleChangeName}
                onBlur={() => handleBlurName('name')}
                supportText={setErrorMsg()}
                showErrorIcon={isNameInputValid}
              />
            </Box>
          </Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <Box className="custom-text-field">
              <Typography
                className="typo-text body3_size input-label"
                data-testid="input-label-propagate-tag"
                tabIndex={0}
              >
                {t(`${enBulkTag}.propagateTag`)}&nbsp;
                <ArrTooltipWrapper
                  trigger={
                    <InfoIcon
                      className="info-icon-label"
                      data-testid="toggle-info-tooltip"
                    />
                  }
                  title={t(`${enBulkTag}.propagateTag`)}
                >
                  {t(`${enBulkTag}.propagateTagInfo`)}
                </ArrTooltipWrapper>
                :
              </Typography>
              <ArrMultiSelectDropdown
                dropdownLabel={t('common.select')}
                dropdownItems={options}
                selectedValues={propagateTag}
                onSelectionChange={handlePropagateChange}
                multiple
                searchable={false}
                withControls
                sizeVariant="medium"
                checkboxDataTestId="new-tag-propagate-tag-dropdown-checkbox"
                onClearAll={() => {}}
                onApply={() => {}}
                isShowTooltip={false}
                dataTestId="msd-new-tag-select"
              />
            </Box>
          </Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <Box className="custom-text-field">
              <Typography
                className="typo-text body3_size input-label"
                data-testid="input-label-tag-classification"
                tabIndex={0}
              >
                {t(`${enBulkTag}.tagClassification`)}&nbsp;
                <ArrTooltipWrapper
                  trigger={
                    <InfoIcon
                      className="info-icon-label"
                      data-testid="toggle-info-tooltip"
                    />
                  }
                  title={t(`${enBulkTag}.tagClassificationInfoTitle`)}
                >
                  {t(`${enBulkTag}.tagClassificationInfo`)}
                </ArrTooltipWrapper>
                :
              </Typography>
              {isTagDetailsLoading ? (
                <ArrLoadingSpinner />
              ) : (
                <ArrSingleSelectDropdown
                  dropdownLabel={t('common.select')}
                  dropdownItems={classificationOptions}
                  selectedValues={tagClassification}
                  onSelectionChange={setTagClassification}
                  sizeVariant="medium"
                  clearSelection
                  dataTestId="asd-new-tag"
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box className="tree-heading-box">
        <Typography
          className="typo-text subhead4_size"
          data-testid="select-parent-for-new-tag"
          tabIndex={0}
        >
          {t(`${enBulkTag}.selectParentForNewTag`)}
        </Typography>
      </Box>
      <Box className="tag-tree-box new-tree" data-testid="new-tag-tree">
        {isLoading ||
        (!searchInputNewTags &&
          (!treeData.length || !defaultExpandedIds.length)) ? (
          <ArrLoadingSpinner />
        ) : (
          <ArrTreeView
            key={treeKey}
            itemsList={cleanedTreeData}
            selectedItem={selectedNewTag}
            handleNoResultMessage={t(`${enBulkTag}.noResult`)}
            allowGlobalSelect={true}
            treeSelectedItems={[]}
            multipleSelection={false}
            isShowCountLabel={false}
            isShowCheckbox={false}
            showActions={false}
            customContentHeight={36}
            setSelectedItem={handleSelected}
            searchKeyword={searchInputNewTags}
            defaultExpandedIds={defaultExpandedIds}
            setExpandedFolders={setDefaultExpandedIds}
          />
        )}
      </Box>
    </div>
  );
};

export default NewTag;
